import {
  Box,
  Checkbox,
  Flex,
  Grid,
  Select,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { t } from "i18next";
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { TextField } from "../../fields/TextField";
import { PurchaseButton } from "./PurchaseButton";
import {
  getSavedAddresses,
  GET_SAVED_ADDRESSES_QUERY_KEY,
  SavedAddress,
  ShippingAddress,
  getTaxAndShipping,
  TaxShippingResponse,
} from "../api/paymentApi";
import axios from "axios";
import { ServerResponse } from "../../../types";
import { COUNTRIES } from "../constants";

interface AddressStepProps {
  address: ShippingAddress;
  setAddress: React.Dispatch<React.SetStateAction<ShippingAddress>>;
  saveAddress: boolean;
  setSaveAddress: (save: boolean) => void;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  validateShippingAddress: () => boolean;
  productId?: number;
  productPrice?: number;
  onTaxShippingSuccess: (data: TaxShippingResponse) => void;
}

export function AddressStep({
  address,
  setAddress,
  saveAddress,
  setSaveAddress,
  errors,
  setErrors,
  validateShippingAddress,
  productId,
  productPrice,
  onTaxShippingSuccess,
}: AddressStepProps) {
  const [isLoadingTaxShipping, setIsLoadingTaxShipping] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // Fetch saved default address
  const { data: savedAddressResponse, isLoading } = useQuery({
    queryKey: GET_SAVED_ADDRESSES_QUERY_KEY,
    queryFn: () => getSavedAddresses(),
    retry: false,
  });

  const savedAddress = savedAddressResponse?.data?.payload?.results;
  const hasSavedAddress = !!savedAddress && !!savedAddress.id;

  // State to track if user wants to use saved address
  const [useSavedAddress, setUseSavedAddress] = React.useState(false);

  // Convert SavedAddress to ShippingAddress
  const convertToShippingAddress = (saved: SavedAddress): ShippingAddress => ({
    first_name: saved.firstname,
    last_name: saved.lastname,
    address_line1: saved.address_line1,
    address_line2: saved.address_line2,
    city: saved.city,
    state: saved.state,
    zip: saved.zip,
    country_id: saved.country_id,
    phone_number: saved.phone_number,
  });

  // Load saved address when user chooses to use it
  React.useEffect(() => {
    if (useSavedAddress && savedAddress) {
      setAddress(convertToShippingAddress(savedAddress));
      setSaveAddress(false); // Don't pre-check save for existing addresses
    } else if (!useSavedAddress && hasSavedAddress && savedAddress) {
      // Clear the form when switching to new address (only if it's currently showing saved address)
      const currentMatchesSaved =
        address.first_name === savedAddress.firstname &&
        address.last_name === savedAddress.lastname &&
        address.address_line1 === savedAddress.address_line1;

      if (currentMatchesSaved) {
        setAddress({
          first_name: "",
          last_name: "",
          address_line1: "",
          address_line2: "",
          city: "",
          state: "",
          zip: "",
          country_id: "us",
          phone_number: "",
        });
      }
    }
  }, [useSavedAddress, savedAddress]);

  const handleContinue = async () => {
    // Clear any previous errors
    setApiError(null);
    setErrors({});

    // First validate the address fields
    if (!validateShippingAddress()) {
      return;
    }

    // Check if we have the required product info
    if (!productId || !productPrice) {
      setApiError(t("components.payments.pod.errors.missingProductInfo"));
      return;
    }

    setIsLoadingTaxShipping(true);

    try {
      const response = await getTaxAndShipping({
        product_id: productId,
        price: productPrice,
        address_line1: address.address_line1,
        address_line2: address.address_line2 || undefined,
        city: address.city,
        state: address.state || undefined,
        zip: address.zip,
        country_id: address.country_id,
      });

      const data = response.data?.payload?.results;
      if (data) {
        onTaxShippingSuccess(data);
      } else {
        setApiError(t("components.payments.pod.errors.taxShippingFailed"));
      }
    } catch (error) {
      let errorMessage = t("components.payments.pod.errors.taxShippingFailed");
      if (axios.isAxiosError<ServerResponse<string>>(error)) {
        const serverError = error.response?.data?.payload?.results;
        if (serverError) {
          errorMessage = serverError;
        }
      }
      setApiError(errorMessage);
    } finally {
      setIsLoadingTaxShipping(false);
    }
  };

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" minHeight="200px">
        <Spinner size="lg" color="blaze.blaze" />
      </Flex>
    );
  }

  return (
    <>
      {/* Saved Address Toggle */}
      {hasSavedAddress && (
        <>
          <Box>
            <Flex
              gap="12px"
              p="16px"
              bg="transparent.white.10"
              borderRadius="16px"
              alignItems="center"
              cursor="pointer"
              onClick={() => setUseSavedAddress(!useSavedAddress)}
              _hover={{ bg: "transparent.white.20" }}
              transition="background 0.2s"
            >
              <Checkbox
                isChecked={useSavedAddress}
                onChange={(e) => setUseSavedAddress(e.target.checked)}
                size="lg"
                variant="blaze"
                pointerEvents="none"
              />
              <Box flex="1">
                <Text fontSize="14px" fontWeight="600" color="dune.600">
                  {t("components.payments.pod.useSavedAddress")}
                </Text>
                <Text fontSize="12px" color="dune.400" mt="2px">
                  {savedAddress.display_name ||
                    `${savedAddress.firstname} ${savedAddress.lastname} - ${savedAddress.address_line1}`}
                </Text>
              </Box>
            </Flex>
          </Box>

          {/* Divider */}
          <Box height="1px" bg="transparent.white.10" />
        </>
      )}

      {/* Address Form - disabled when using saved address */}
      <Grid templateColumns="repeat(2, 1fr)" gap="16px">
        <TextField
          type="text"
          placeholder={t("components.payments.pod.firstName")}
          value={address.first_name}
          onChange={(value) =>
            setAddress((prev) => ({ ...prev, first_name: value }))
          }
          error={errors.first_name}
          autoComplete="given-name"
          isDisabled={useSavedAddress}
        />
        <TextField
          type="text"
          placeholder={t("components.payments.pod.lastName")}
          value={address.last_name}
          onChange={(value) =>
            setAddress((prev) => ({ ...prev, last_name: value }))
          }
          error={errors.last_name}
          autoComplete="family-name"
          isDisabled={useSavedAddress}
        />
      </Grid>

      <TextField
        type="text"
        placeholder={t("components.payments.pod.addressLine1")}
        value={address.address_line1}
        onChange={(value) =>
          setAddress((prev) => ({ ...prev, address_line1: value }))
        }
        error={errors.address_line1}
        autoComplete="address-line1"
        isDisabled={useSavedAddress}
      />

      <TextField
        type="text"
        placeholder={t("components.payments.pod.addressLine2")}
        value={address.address_line2 || ""}
        onChange={(value) =>
          setAddress((prev) => ({ ...prev, address_line2: value }))
        }
        autoComplete="address-line2"
        isDisabled={useSavedAddress}
      />

      <Grid templateColumns="repeat(2, 1fr)" gap="16px">
        <TextField
          type="text"
          placeholder={t("components.payments.pod.city")}
          value={address.city}
          onChange={(value) => setAddress((prev) => ({ ...prev, city: value }))}
          error={errors.city}
          autoComplete="address-level2"
          isDisabled={useSavedAddress}
        />
        <TextField
          type="text"
          placeholder={t("components.payments.pod.state")}
          value={address.state || ""}
          onChange={(value) =>
            setAddress((prev) => ({ ...prev, state: value }))
          }
          autoComplete="address-level1"
          isDisabled={useSavedAddress}
        />
      </Grid>

      <Grid templateColumns="repeat(2, 1fr)" gap="16px">
        <TextField
          type="text"
          placeholder={t("components.payments.pod.zip")}
          value={address.zip}
          onChange={(value) => setAddress((prev) => ({ ...prev, zip: value }))}
          error={errors.zip}
          autoComplete="postal-code"
          isDisabled={useSavedAddress}
        />
        <Select
          value={address.country_id}
          onChange={(e) =>
            setAddress((prev) => ({
              ...prev,
              country_id: e.target.value,
            }))
          }
          bg="transparent.white.10"
          borderRadius="16px"
          border="none"
          color="dune.600"
          fontFamily="Roboto"
          fontSize="16px"
          height="48px"
          isDisabled={useSavedAddress}
        >
          {COUNTRIES.map((country) => (
            <option
              key={country.id}
              value={country.id}
              style={{ background: "#1a1b1e" }}
            >
              {country.name}
            </option>
          ))}
        </Select>
      </Grid>

      <TextField
        type="tel"
        placeholder={t("components.payments.pod.phoneNumber")}
        value={address.phone_number || ""}
        onChange={(value) =>
          setAddress((prev) => ({ ...prev, phone_number: value }))
        }
        autoComplete="tel"
        isDisabled={useSavedAddress}
      />

      {/* Save Address Checkbox - only show when NOT using saved address */}
      {!useSavedAddress && (
        <Checkbox
          isChecked={saveAddress}
          onChange={(e) => setSaveAddress(e.target.checked)}
          size="lg"
          variant="blaze"
          fontSize="14px"
        >
          <Text fontSize="14px" color="dune.600">
            {t("components.payments.pod.rememberThisAddress")}
          </Text>
        </Checkbox>
      )}

      {/* API Error Display */}
      {apiError && (
        <Text fontSize="14px" color="red.400" textAlign="center">
          {apiError}
        </Text>
      )}

      <PurchaseButton
        text={t("components.payments.pod.continueToPayment")}
        onClick={handleContinue}
        loading={isLoadingTaxShipping}
      />
    </>
  );
}
